import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useAuth, formatApiErrorDetail } from '../context/AuthContext';

const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5" aria-hidden="true">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15A11 11 0 0 0 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z" />
  </svg>
);

export default function AuthPage({ mode }) {
  const isLogin = mode === 'login';
  const navigate = useNavigate();
  const { login, register } = useAuth();
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/account';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (isLogin) {
        await login(form.email, form.password);
      } else {
        await register(form.name, form.email, form.password);
      }
      navigate('/account');
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const inputClass = 'w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 text-gray-900';

  return (
    <>
      <Helmet>
        <title>{isLogin ? 'Login' : 'Create Account'} | Harpalyk</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      <Header />
      <main className="pt-20 min-h-screen bg-gray-50 flex items-center justify-center px-6 py-16" data-testid={`${mode}-page`}>
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 w-full max-w-md">
          <h1 className="text-2xl font-bold text-gray-900 mb-1">{isLogin ? 'Welcome back' : 'Create your account'}</h1>
          <p className="text-gray-500 text-sm mb-6">
            {isLogin ? 'Sign in to view your orders and track purchases.' : 'Register to track orders and checkout faster.'}
          </p>

          <button onClick={handleGoogle} data-testid="google-login-btn" className="w-full flex items-center justify-center gap-3 border border-gray-300 hover:bg-gray-50 rounded-lg py-3 font-medium text-gray-700 transition-all mb-5">
            <GoogleIcon /> Continue with Google
          </button>

          <div className="flex items-center gap-3 mb-5">
            <div className="flex-1 h-px bg-gray-200" />
            <span className="text-xs text-gray-400 uppercase">or</span>
            <div className="flex-1 h-px bg-gray-200" />
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <input type="text" required placeholder="Full name" value={form.name} data-testid="auth-name-input"
                onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputClass} />
            )}
            <input type="email" required placeholder="Email address" value={form.email} data-testid="auth-email-input"
              onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputClass} />
            <input type="password" required minLength={6} placeholder="Password" value={form.password} data-testid="auth-password-input"
              onChange={(e) => setForm({ ...form, password: e.target.value })} className={inputClass} />

            {error && <p className="text-sm text-red-600" data-testid="auth-error">{error}</p>}

            <button type="submit" disabled={loading} data-testid="auth-submit-btn"
              className="w-full bg-emerald-600 hover:bg-emerald-500 disabled:opacity-60 text-white py-3 rounded-lg font-semibold transition-all">
              {loading ? 'Please wait…' : isLogin ? 'Sign In' : 'Create Account'}
            </button>
          </form>

          <p className="text-sm text-gray-500 mt-6 text-center">
            {isLogin ? (
              <>New to Harpalyk? <Link to="/register" data-testid="goto-register-link" className="text-emerald-600 font-medium hover:underline">Create an account</Link></>
            ) : (
              <>Already have an account? <Link to="/login" data-testid="goto-login-link" className="text-emerald-600 font-medium hover:underline">Sign in</Link></>
            )}
          </p>
        </div>
      </main>
      <Footer />
    </>
  );
}
